import { z } from "zod";

/**
 * Membership + Hour-Bank Engine (Phase 2b) — mirrors app/schemas/membership.py.
 */

export const MembershipStatusSchema = z.enum(["pending", "active", "paused", "cancelled"]);
export type MembershipStatus = z.infer<typeof MembershipStatusSchema>;

export const MEMBERSHIP_STATUS_LABELS: Record<MembershipStatus, string> = {
  pending: "Pending",
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
};

export const MEMBERSHIP_STATUS_BADGE_VARIANT: Record<
  MembershipStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  pending: "warning",
  active: "success",
  paused: "secondary",
  cancelled: "danger",
};

export const StudioSessionSourceSchema = z.enum(["membership", "day_pass", "pass_pack"]);
export type StudioSessionSource = z.infer<typeof StudioSessionSourceSchema>;

export const STUDIO_SESSION_SOURCE_LABELS: Record<StudioSessionSource, string> = {
  membership: "Membership hours",
  day_pass: "Day pass",
  pass_pack: "Pass pack",
};

export const StudioSessionStatusSchema = z.enum(["confirmed", "cancelled", "completed"]);
export type StudioSessionStatus = z.infer<typeof StudioSessionStatusSchema>;

export const DayPassSourceSchema = z.enum(["single", "pack"]);
export type DayPassSource = z.infer<typeof DayPassSourceSchema>;

// ---- Membership tiers ------------------------------------------------------

export const MembershipTierSchema = z.object({
  id: z.string(),
  name: z.string(),
  price_cents: z.number(),
  weekly_hour_quota: z.number().nullable(),
  storage: z.boolean(),
  min_term_months: z.number(),
  active: z.boolean(),
  stripe_price_id: z.string().nullable(),
  // Staff-visible only — this schema mirrors MembershipTierOut, served
  // solely by the Bearer-JWT/default-deny-gated GET /api/v2/memberships/tiers.
  // NEVER add this field to any portal-scoped (token-gated or public) type
  // in this file — see the "Portal" section below and
  // app/models/membership_tier.py's docstring for the hard visibility
  // requirement this field is otherwise subject to.
  addon_hour_rate_cents: z.number().nullable(),
});
export type MembershipTier = z.infer<typeof MembershipTierSchema>;

export const MembershipTierListResponseSchema = z.object({ items: z.array(MembershipTierSchema) });

// ---- Memberships ------------------------------------------------------------

export const MembershipSchema = z.object({
  id: z.string(),
  contact_id: z.string(),
  tier_id: z.string(),
  tier_name: z.string(),
  status: MembershipStatusSchema,
  started_on: z.string().nullable(),
  min_term_ends_on: z.string().nullable(),
  stripe_subscription_id: z.string().nullable(),
  storage_assigned: z.boolean(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const MembershipListResponseSchema = z.object({ items: z.array(MembershipSchema) });

export const MembershipActivateResponseSchema = z.object({
  membership: MembershipSchema,
  billing_mode: z.string(),
});
export type MembershipActivateResponse = z.infer<typeof MembershipActivateResponseSchema>;

// ---- Hour balance ------------------------------------------------------------

export const HourBalanceSchema = z.object({
  membership_id: z.string(),
  week_start: z.string(),
  week_end: z.string(),
  quota: z.number().nullable(),
  used: z.number(),
  remaining: z.number().nullable(),
});
export type HourBalance = z.infer<typeof HourBalanceSchema>;

// ---- Studio sessions ----------------------------------------------------------

export const StudioSessionSchema = z.object({
  id: z.string(),
  membership_id: z.string().nullable(),
  contact_id: z.string(),
  resource_id: z.string(),
  session_date: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  hours: z.number(),
  source: StudioSessionSourceSchema,
  status: StudioSessionStatusSchema,
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type StudioSession = z.infer<typeof StudioSessionSchema>;

export const StudioSessionListResponseSchema = z.object({ items: z.array(StudioSessionSchema) });

export const StudioWindowOccupancySchema = z.object({
  window_index: z.number(),
  start_time: z.string(),
  end_time: z.string(),
  booked: z.number(),
  capacity: z.number(),
  seats_free: z.number(),
});
export type StudioWindowOccupancy = z.infer<typeof StudioWindowOccupancySchema>;

export const StudioOccupancySchema = z.object({
  resource_id: z.string(),
  session_date: z.string(),
  windows: z.array(StudioWindowOccupancySchema),
});
export type StudioOccupancy = z.infer<typeof StudioOccupancySchema>;

// ---- Day passes / pass packs ----------------------------------------------------

export const DayPassSchema = z.object({
  id: z.string(),
  contact_id: z.string(),
  pass_date: z.string(),
  source: DayPassSourceSchema,
  pack_id: z.string().nullable(),
  price_cents: z.number(),
  invoice_id: z.string().nullable(),
  created_at: z.string(),
});
export type DayPass = z.infer<typeof DayPassSchema>;

export const PassPackSchema = z.object({
  id: z.string(),
  contact_id: z.string(),
  purchased_at: z.string(),
  expires_at: z.string(),
  passes_total: z.number(),
  passes_used: z.number(),
  price_cents: z.number(),
  invoice_id: z.string().nullable(),
  created_at: z.string(),
});
export type PassPack = z.infer<typeof PassPackSchema>;

// ---- Portal ---------------------------------------------------------------------

export const MembershipPortalMeSchema = z.object({
  status: z.string(),
  started_on: z.string().nullable(),
  min_term_ends_on: z.string().nullable(),
  storage_assigned: z.boolean(),
  tier: z.object({
    name: z.string(),
    weekly_hour_quota: z.number().nullable(),
    storage: z.boolean(),
  }),
  balance: z.object({
    week_start: z.string(),
    week_end: z.string(),
    quota: z.number().nullable(),
    used: z.number(),
    remaining: z.number().nullable(),
  }),
  upcoming_sessions: z.array(
    z.object({
      id: z.string(),
      session_date: z.string(),
      start_time: z.string(),
      end_time: z.string(),
      hours: z.number(),
      status: z.string(),
    }),
  ),
});
export type MembershipPortalMe = z.infer<typeof MembershipPortalMeSchema>;

// ---- Self-service add-on studio hours (migration 009) ----------------------
// Portal-only types — app/api/portal_membership.py's new addon-hours routes.
// `MembershipPortalAddonOptionsSchema` is the ONLY place `rate_cents_per_hour`
// / block sizes appear anywhere in the frontend: it is fetched exclusively by
// features/portal/useMembershipPortal.ts (token-scoped), never by any staff
// hook/component. Do not reuse this schema or its rate field outside the
// membership portal page — see app/models/membership_tier.py's docstring for
// the hard "don't let a prospect compare pricing" requirement this mirrors.

export const MembershipPortalAddonOptionsSchema = z.object({
  available: z.boolean(),
  rate_cents_per_hour: z.number().nullable(),
  block_sizes: z.array(z.number()),
});
export type MembershipPortalAddonOptions = z.infer<typeof MembershipPortalAddonOptionsSchema>;

export const MembershipPortalAddonSchema = z.object({
  id: z.string(),
  week_start: z.string(),
  hours: z.number(),
  unit_price_cents: z.number(),
  price_cents: z.number(),
  invoice_id: z.string().nullable(),
  created_at: z.string(),
});
export type MembershipPortalAddon = z.infer<typeof MembershipPortalAddonSchema>;

export const MembershipPortalAddonListSchema = z.object({ items: z.array(MembershipPortalAddonSchema) });

export const MembershipPortalAddonPurchaseResponseSchema = z.object({
  id: z.string(),
  week_start: z.string(),
  hours: z.number(),
  unit_price_cents: z.number(),
  price_cents: z.number(),
  invoice_id: z.string().nullable(),
  billing_mode: z.string(),
  checkout_url: z.string().nullable(),
});
export type MembershipPortalAddonPurchaseResponse = z.infer<typeof MembershipPortalAddonPurchaseResponseSchema>;

export interface PurchaseMembershipPortalAddonHoursInput {
  week_start: string;
  hours: 2 | 4 | 6;
}

// ---- Mutation inputs --------------------------------------------------------

export interface CreateMembershipInput {
  contact_id: string;
  tier_id: string;
  notes?: string | null;
}

export interface ActivateMembershipInput {
  id: string;
  started_on?: string | null;
}

export interface CancelMembershipInput {
  id: string;
  effective_date?: string | null;
  admin_override?: boolean;
}

export interface BookStudioSessionInput {
  resource_id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  membership_id?: string | null;
  day_pass_id?: string | null;
}

export interface SellDayPassInput {
  contact_id: string;
  pass_date: string;
}

export interface RedeemPassPackInput {
  pack_id: string;
  pass_date: string;
}
